import { useForm, useLatestEntity, useSetState } from '@domain-group/react-hooks'
import type { UseFormPayload } from '@domain-group/react-hooks'
import useSelector from '@hooks/useSelector'

import {
  capitalizeString, deepSetObject, digObject, generateID, toggleArray,
} from '@domain-group/javascript-utils'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { ProviderModel, UserModel } from '@models/types'

import type { ModalCallbacks } from '../index'

export type QuickLink = {
  href: string,
  id: string,
  title: string,
}

type DefaultState = {
  action: 'create' | 'update',
  selectedQuickLink: QuickLink | null,
}

type SetState = (state: Partial<DefaultState>) => void

type ResetMyLinkParams = {
  setEntityState: UseFormPayload['setEntityState'],
  setState: SetState,
}

const resetMyLink = (params: ResetMyLinkParams) => {
  const { setEntityState, setState } = params

  setState({ action: 'create', selectedQuickLink: null })
  setEntityState({ href: '', id: generateID(), title: '' })
}

type UpdateUserOptionsParams = {
  action: string,
  entity: ProviderModel | UserModel,
  quickLink: UseFormPayload['entityState'],
}

const updateEntityOptions = (params: UpdateUserOptionsParams) => {
  const { action, quickLink, entity } = params

  const entityQuickLinks = digObject(entity, 'options.quick_links', [])

  const options = action === 'delete' ? { deepCompare: true } : { useObjectKey: 'id' }
  const updatedQuickLinksArray = toggleArray(entityQuickLinks, quickLink, options)
  // @ts-expect-error TS2345
  const updatedEntity = deepSetObject(entity, 'options.quick_links', updatedQuickLinksArray)

  return updatedEntity
}

type ManageEntityQuickLinkParams = {
  action: string,
  entity: ProviderModel | UserModel,
  quickLink: UseFormPayload['entityState'],
  setEntityState: UseFormPayload['setEntityState'],
  setState: SetState,
  updateEntity: (payload: HandleCallbackActionParams) => Promise<object>,
  updateFn: () => void,
}

const manageEntityQuickLink = (params: ManageEntityQuickLinkParams) => {
  const {
    action, entity, quickLink, setEntityState, setState, updateEntity, updateFn,
  } = params

  const updatedEntity = updateEntityOptions({ action, quickLink, entity })

  const entityPayload = {
    callbacks: {
      action: updateFn,
      afterAction: () => resetMyLink({ setEntityState, setState }),
    },
    entityParams: updatedEntity,
    toastText: `Quick Link ${capitalizeString(action)}d`,
  }
  // @ts-expect-error TS2322
  updateEntity(entityPayload as HandleCallbackActionParams)
}

const requiredFields = [
  { key: 'href' },
  { key: 'title' },
]

const defaultQuickLinkState = {
  href: '',
  id: generateID(),
  title: '',
}

const defaultState: DefaultState = {
  action: 'create',
  selectedQuickLink: null,
}

interface useManageEntityQuickLinksModalParams {
  callbacks: {
    updateFn: () => Promise<object>,
  },
  entity: ProviderModel | UserModel,
  entityKey: string,
  modalCallbacks: ModalCallbacks,
}

const useManageEntityQuickLinksModal = (params: useManageEntityQuickLinksModalParams) => {
  const {
    callbacks: {
      updateFn,
    },
    entity: initEntity,
    entityKey,
    modalCallbacks: {
      updateEntity,
    },
  } = params || {}

  const { entity }: { entity: ProviderModel | UserModel} = useLatestEntity(initEntity, entityKey)

  const [state, setState] = useSetState(defaultState)
  const { action: actionString, selectedQuickLink } = state

  const formPayload = useForm(
    defaultQuickLinkState,
    // @ts-expect-error TS2322
    { entity: selectedQuickLink, requiredFields, validateOn: 'change' },
    [selectedQuickLink?.id],
  )

  // @ts-expect-error TS7053
  const updating = useSelector(reduxState => reduxState[entityKey]?.updating)

  const { setEntityState } = formPayload

  const quickLinks: QuickLink[] = digObject(entity, 'options.quick_links', [])
  const hasQuickLinks = !!quickLinks.length

  return {
    actionString,
    callbacks: {
      manageEntityQuickLink: (action: string, quickLink: UseFormPayload['entityState']) => manageEntityQuickLink({
        action, entity, quickLink, setEntityState, setState, updateEntity, updateFn,
      }),
      selectQuickLink: (quickLink: QuickLink) => setState({
        action: 'update', selectedQuickLink: quickLink,
      }),
    },
    formPayload,
    hasQuickLinks,
    quickLinks,
    updating,
  }
}

export default useManageEntityQuickLinksModal
